import React, { useEffect, useState } from "react";
import { animate, motion, useReducedMotion } from "framer-motion";
import { RankEmblem } from "./RankEmblem";
import { RankBar, labelOf } from "./rankParts";
import { CALIBRATION_GAMES, promotion, rankInfo } from "../../lib/ranks";
import { t } from "../../lib/i18n";

/**
 * What the game won or lost the player, on the end screens (WinnerCelebration, the round summary
 * once they are out): their place, the points counting up or down to the new total, the bar
 * filling, and a promotion when they reach a new division or rank. `result` is the server's
 * `rank_results[me]` ({kind, place, of, before, after, delta, games}).
 */
export function RankChange({ result, className = "" }) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? result.after : result.before);

  useEffect(() => {
    if (reduce) { setShown(result.after); return undefined; }
    const ctl = animate(result.before, result.after, {
      duration: 1.3, delay: 0.6, ease: "easeOut", onUpdate: (v) => setShown(Math.round(v)),
    });
    return () => ctl.stop();
  }, [result.before, result.after, reduce]);

  const before = { rp: result.before, games: result.games - 1 };
  const after = { rp: result.after, games: result.games };
  const now = rankInfo({ rp: shown, games: result.games });
  const placed = result.games === CALIBRATION_GAMES; // the last calibration game: the rank shows
  const up = !rankInfo(before).calibrating && promotion(before, after);
  const headline = placed ? t("rank.change.placed") : up === "tier" ? t("rank.change.newTier") : up ? t("rank.change.promoted") : null;
  const kind = result.kind === "win"
    ? t(result.place === 2 ? "rank.change.winSecond" : "rank.change.win") // the 2nd winner of a table of 4
    : result.kind === "abandon"
    ? t("rank.change.abandon")
    : t("rank.change.loss", { place: result.place, of: result.of });
  const gain = result.delta >= 0;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border px-3 py-2.5 ${headline ? "border-amber-400/50 bg-amber-400/10" : "border-white/[0.06] bg-slate-800/55"} ${className}`}
      data-testid="rank-change"
    >
      {headline && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9 }}
          className="mb-1 text-center text-[11px] uppercase tracking-[0.2em] text-amber-300"
          data-testid="rank-promotion"
        >
          {headline}
        </motion.div>
      )}
      <div className="flex items-center gap-3">
        <motion.div
          key={`${now.tier}${now.division}${now.calibrating}`}
          initial={{ scale: 0.7, opacity: 0.4 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 380, damping: 16 }}
        >
          <RankEmblem info={now} size={42} />
        </motion.div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate font-display text-base italic text-amber-200">{labelOf(now)}</span>
            <span className={`shrink-0 text-lg tabular-nums ${gain ? "text-emerald-300" : "text-rose-300"}`} data-testid="rank-delta">
              {t(gain ? "rank.gain" : "rank.drop", { n: Math.abs(result.delta) })}
            </span>
          </div>
          <RankBar
            info={now.calibrating ? { progress: result.games / CALIBRATION_GAMES } : now}
            className="mt-1"
          />
          <div className="mt-1 flex justify-between gap-2 text-[10px] text-slate-400">
            <span>{kind}</span>
            <span className="tabular-nums">
              {now.calibrating
                ? t("rank.change.calibration", { n: result.games, total: CALIBRATION_GAMES })
                : t("rank.pr", { n: shown })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RankChange;
